import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Chip,
	Divider,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
    TablePagination,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	addTicketComment,
	assignTicket,
	assignTicketToDevelopers,
	getTicketComments,
	updateTicketStatus,
	viewAllErrorTickets,
} from "../../actions/form";
import { ESRState } from "../../types/data";

import { TicketComment } from "@/types/action";
import { Add, ExpandMore } from "@mui/icons-material";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { ErrorReportModal } from "../ErrorReportModal";

interface ErrorTicket {
	id: number;
	ticket_number: string;
	creator_id: string;
	description: string;
	ticket_type: string;
	status: string;
	assigned_to: string;
	esr_id: string;
	question_id: string;
	created_at: string;
	updated_at: string;
	shared_to_dev: boolean;
	behalf_of_id: string;
}

export const ErrorTickets = (): JSX.Element => {
	const dispatch = useDispatch();
	const [user, setUser] = useCurrentUser();
	const [openErrorModal, setOpenErrorModal] = useState(false);
	const errorTickets = useSelector((state: ESRState) => state.error_tickets);
	const isAdmin = user?.groups?.includes("Admin");
	const [commentText, setCommentText] = useState<Record<number, string>>({});
	const [expandedTicketId, setExpandedTicketId] = useState<number | null>(null);
	const comments = useSelector((state: ESRState) => state.ticket_comments);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState("All Statuses");
    const [ticketTypeFilter, setTicketTypeFilter] = useState("All Types");
    const filteredTickets = errorTickets.filter(
        (ticket: ErrorTicket) => {
            const search = searchText.toLowerCase().trim();
            return (
                (statusFilter === "All Statuses" ||
                    ticket.status === statusFilter) &&
                (ticketTypeFilter === "All Types" ||
                    ticket.ticket_type === ticketTypeFilter) &&
                (!search ||
                    ticket.ticket_number.toLowerCase().includes(search) ||
                    ticket.assigned_to.toLowerCase().includes(search))
            );
        },
    );

    const maxPage = Math.max(
        0,
        Math.ceil(filteredTickets.length / rowsPerPage) - 1,
    );

    const currentPage = Math.min(page, maxPage);
    const paginatedTickets = filteredTickets.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage,
    );

	const getStatusColor = (status: string) => {
		switch (status) {
			case "OPEN":
				return "error";
			case "IN_PROGRESS":
				return "warning";
			case "WAITING_FOR_USER_RESPONSE":
				return "info";
			case "RESOLVED":
				return "success";
			case "CLOSED":
				return "default";
			default:
				return "default";
		}
	};
	const formatStatus = (status: string) =>
    status
        .toLowerCase()
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

	useEffect(() => {
		dispatch(viewAllErrorTickets(
        statusFilter === "CLOSED" ? "CLOSED" : undefined
    ));
	}, [statusFilter]);

    useEffect(() => {
        setPage(0);
    }, [searchText, statusFilter, ticketTypeFilter]);

	const handleAccordionChange =
		(ticketId: number) => (_event: React.SyntheticEvent, expanded: boolean) => {
			if (expanded) {
				setExpandedTicketId(ticketId);

				dispatch(getTicketComments(ticketId));
			} else {
				setExpandedTicketId(null);
			}
		};

	return (
		<>
			<Box sx={{ display: "flex", height: "100%" }}>
				<Box
					sx={{
						flex: "auto",
						p: "60px",
						height: "100%",
						boxSizing: "border-box",
						overflow: "scroll",
					}}
				>
					<Box sx={{ mb: 4 }}>
                        {/* Header row */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="h4" fontWeight={600}>
                                Error Tickets
                            </Typography>

                            <Button
                                variant="contained"
                                onClick={() => setOpenErrorModal(true)}
                            >
								<Add sx={{ mr: "8px" }} />
                                Create Ticket
                            </Button>
                        </Box>

                        {/* Search and filters row */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <TextField
                                size="small"
                                placeholder="Search by ticket number or assigned person"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{
                                    minWidth: 300,
                                    "& input::placeholder": {
                                        fontSize: "0.8rem",
                                    },
                                }}
                            />

                            <Select
                                size="small"
                                defaultValue=""
                                displayEmpty
                                renderValue={(value) => value ? String(value) : "All Statuses"}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                sx={{minWidth: 180}}
                            >
                                <MenuItem value="All Statuses">All Statuses</MenuItem>
                                <MenuItem value="OPEN">Open</MenuItem>
                                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                                <MenuItem value="WAITING_FOR_USER_RESPONSE">
                                    Waiting for More Info
                                </MenuItem>
                                <MenuItem value="RESOLVED">Resolved</MenuItem>
                                <MenuItem value="CLOSED">Closed</MenuItem>
                            </Select>

                            <Select
                                size="small"
                                defaultValue=""
                                displayEmpty
                                renderValue={(value) => value ? String(value) : "All Types"}
                                onChange={(e) => setTicketTypeFilter(e.target.value)}
                                sx={{minWidth: 180}}
                            >
                                <MenuItem value="All Types">All Types</MenuItem>
                                <MenuItem value="LOGIN">Login</MenuItem>
                                <MenuItem value="ESR_FORM">ESR Form</MenuItem>
								<MenuItem value="QID_MODIFY_RESPONSE">Modify Question Response</MenuItem>
                                <MenuItem value="REQUEST_PERMISSIONS">
                                    Request Permissions
                                </MenuItem>
                                <MenuItem value="PROJECT_MISSING">
                                    Project Missing
                                </MenuItem>
                                <MenuItem value="OTHER">Other</MenuItem>
                            </Select>
                        </Box>
                    </Box>
						
					{paginatedTickets.map((ticket: ErrorTicket) => {
						const isCreator =
							user?.email?.toLowerCase() === ticket.creator_id?.toLowerCase();
						const isAssignedToMe =
							user?.email?.toLowerCase() === ticket.assigned_to?.toLowerCase();
						const canAdminUpdateStatus = isAdmin && isAssignedToMe;
						const canCreatorClose = isCreator && ticket.status === "RESOLVED";
						return (
							<Accordion
								key={ticket.id}
								expanded={expandedTicketId === ticket.id}
								onChange={handleAccordionChange(ticket.id)}
								sx={{
									mb: 2,
									"&:before": {
										display: "none",
									},
								}}
							>
								<AccordionSummary expandIcon={<ExpandMore />}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											width: "100%",
										}}
									>
										<Box>
											<Typography fontWeight={700}>
												{ticket.ticket_number}
											</Typography>

											<Stack
												direction="row"
												spacing={1}
												sx={{ mt: 0.5, flexWrap: "wrap" }}
											>
												<Chip
													label={`Type: ${ticket.ticket_type}`}
													size="small"
												/>

												{ticket.assigned_to && (
													<Chip
														label={`Assigned: ${ticket.assigned_to}`}
														size="small"
													/>
												)}

												{ticket.shared_to_dev && (
													<Chip
														label="Assigned To Developer"
														size="small"
													/>
												)}
											</Stack>
										</Box>

										<Chip
											label={formatStatus(ticket.status)}
											color={getStatusColor(ticket.status)}
										/>
									</Box>
								</AccordionSummary>

								<AccordionDetails>
									<Stack spacing={2}>
										<Typography>
											<strong>Type:</strong> {ticket.ticket_type}
										</Typography>

										<Typography>
											<strong>Created By:</strong> {ticket.creator_id}
										</Typography>

										{
											ticket.behalf_of_id.length > 0 && (
												<Typography>
													<strong>Created For:</strong> {ticket.behalf_of_id}
												</Typography>
											)
										}

										<Typography>
											<strong>Description:</strong>
										</Typography>

										<Paper sx={{ p: 2 }}>{ticket.description}</Paper>

										{ticket.esr_id && (
											<Typography>
												<strong>ESR ID:</strong> {ticket.esr_id}
											</Typography>
										)}

										{ticket.question_id && (
											<Typography>
												<strong>Question ID:</strong> {ticket.question_id}
											</Typography>
										)}

										<Typography variant="caption" color="text.secondary">
											Created{" "}
											{dayjs(ticket.created_at).format("MM/DD/YYYY hh:mm A")}
										</Typography>

										<Divider />

										<Typography variant="h6">Ticket Status</Typography>
										{isAdmin &&
											ticket.status === "OPEN" &&
											!ticket.assigned_to && (
												<Button
													variant="contained"
													color="primary"
													onClick={() => {
														dispatch(assignTicket(ticket.id));
													}}
												>
													Assign To Me
												</Button>
											)}
										{isAdmin && ticket.assigned_to && !ticket.shared_to_dev && (
											<Button
												variant="contained"
												color="primary"
												onClick={() => {
													dispatch(assignTicketToDevelopers(ticket.id));
												}}
											>
												Assign To DEV
											</Button>
										)}
										{canAdminUpdateStatus ? (
											<>
												<Select
													value={ticket.status}
													fullWidth
													onChange={(e) =>
														dispatch(
															updateTicketStatus(ticket.id, e.target.value),
														)
													}
												>
													<MenuItem value="OPEN">OPEN</MenuItem>
													<MenuItem value="IN_PROGRESS">IN PROGRESS</MenuItem>
													<MenuItem value="WAITING_FOR_USER_RESPONSE">
														WAITING FOR MORE INFO
													</MenuItem>
													<MenuItem value="RESOLVED">RESOLVED</MenuItem>
												</Select>
												{ticket.status === "RESOLVED" && (
													<Alert severity="info" sx={{ mt: 1 }}>
														The ticket creator has been notified that this
														ticket was resolved. If no action is taken, the
														ticket will be automatically closed 20 days after
														being marked as resolved.
													</Alert>
												)}
											</>
										) : (
											<>
												<Box
													sx={{
														display: "flex",
														justifyContent: "center",
														my: 1,
													}}
												>
													<Chip
														label={formatStatus(ticket.status)}
														color={getStatusColor(ticket.status)}
													/>
												</Box>
												{canCreatorClose && (
													<>
														<Alert severity="success">
															The support team has marked this ticket as
															resolved. Please verify the issue is fixed. If
															everything looks good, click "Close Ticket".
															Otherwise, add a comment and the support team will
															continue investigating.
														</Alert>
														<Button
															variant="contained"
															color="success"
															onClick={() =>
																dispatch(
																	updateTicketStatus(ticket.id, "CLOSED"),
																)
															}
														>
															Close Ticket
														</Button>
														<Alert severity="info" sx={{ mt: 1 }}>
															If no action is taken, this ticket will be
															automatically closed 20 days after it was marked
															as resolved.
														</Alert>
													</>
												)}
											</>
										)}

										<Divider />

										<Typography variant="h6">Comments</Typography>

										{comments[ticket.id]?.length > 0 ? (
											comments[ticket.id].map((comment: TicketComment) => (
												<Paper
													key={comment.id}
													sx={{
														p: 2,
														backgroundColor: "#f7f9fc",
													}}
												>
													<Typography fontWeight={600}>
														{comment.responder_id}
													</Typography>

													<Typography>{comment.comment}</Typography>

													<Typography variant="caption" color="text.secondary">
														{dayjs(comment.created_at).format(
															"MM/DD/YYYY hh:mm A",
														)}
													</Typography>
												</Paper>
											))
										) : (
											<Typography color="text.secondary">
												No comments yet.
											</Typography>
										)}

										{!(isAdmin || ticket.status !== "CLOSED") && (
											<Alert severity="info">
												This ticket has been closed. No further comments may be
												added.
											</Alert>
										)}

										{(isAdmin || ticket.status !== "CLOSED") && (
											<>
												<TextField
													label="Add Comment"
													multiline
													rows={3}
													fullWidth
													value={commentText[ticket.id] || ""}
													onChange={(e) =>
														setCommentText({
															...commentText,
															[ticket.id]: e.target.value,
														})
													}
												/>

												<Button
													variant="contained"
													onClick={() => {
														dispatch(
															addTicketComment(
																ticket.id,
																commentText[ticket.id],
															),
														);
														setCommentText({
															...commentText,
															[ticket.id]: "",
														});
													}}
													disabled={!commentText[ticket.id]?.trim()}
												>
													Add Comment
												</Button>
											</>
										)}
									</Stack>
								</AccordionDetails>
							</Accordion>
						);
					})}
				</Box>
			</Box>

			<ErrorReportModal
				open={openErrorModal}
				onClose={() => {
					setOpenErrorModal(false);
					dispatch(viewAllErrorTickets());
				}}
				creatorid={user?.email}
			/>

            <TablePagination
                component="div"
                count={filteredTickets.length}
                page={currentPage}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
		</>
	);
};
